import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { useToast } from "@/components/Toast";
import { Badge, Button, Card, Empty, ErrorNote, inputClass, Spinner } from "@/components/ui";
import { api, type LeadRow } from "@/lib/api";
import { formatAcres, formatAgo, formatPhone, shown } from "@/lib/format";
import { leadHref, listId } from "@/lib/leadList";
import { DEAD_REASON_HELP, DEAD_REASON_LABEL, DEAD_REASONS } from "@/lib/stages";

/**
 * /archived — every dead lead, with its reason and a way back (D-32).
 *
 * The v1 board carried `dead` and `priced_out` as lanes, which meant the working view of the pipeline
 * grew for ever and the two questions a dead lead can answer were both hard to ask: "how are we losing
 * them" (the reason breakdown) and "did this person come back" (Revive).
 *
 * **A dead lead has not left the system.** It stays in the dedupe index, so re-importing it does not
 * create a second record; it stays in every §7.8 number, so the funnel still credits the progress it
 * made before dying; and `dead_reason = unsubscribed` still feeds the §12 suppression export. What it
 * leaves is the Leads table and the Kanban — the two views whose job is "what should I work on".
 */
function leadName(l: LeadRow): string {
  const person = [l.first_name, l.last_name].filter(Boolean).join(" ");
  return l.alias ?? l.entity_name ?? (person || "(unnamed)");
}

export function Archived() {
  const [params, setParams] = useSearchParams();
  const reasonFilter = params.getAll("dead_reason");
  const q = params.get("q") ?? "";
  const page = Number(params.get("page") ?? 1);
  const sort = params.get("sort") ?? "last_touched_at";
  const order = params.get("order") === "asc" ? "asc" : "desc";
  const list = listId("archived", params);
  const toast = useToast();
  const qc = useQueryClient();

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next);
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ["leads", "archived", { q, reasonFilter, sort, order, page }],
    queryFn: () =>
      api.leads({
        archived: true,
        q: q || undefined,
        dead_reason: reasonFilter.length ? reasonFilter : undefined,
        sort,
        order,
        page,
        page_size: 50,
      }),
  });

  const revive = useMutation({
    mutationFn: (leadId: string) => api.reviveLead(leadId),
    onSuccess: (detail) => {
      toast.push(`${detail.lead.alias ?? leadName(detail.lead)} is back in Follow Up`);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  return (
    <div className="p-4" data-testid="archived">
      <header className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Archived</h1>
          <p className="text-xs text-fg-muted">
            {data ? `${data.total} dead lead${data.total === 1 ? "" : "s"}` : "…"} · still counted in{" "}
            <Link to="/analytics" className="underline">
              analytics
            </Link>{" "}
            and still in the dedupe index
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            defaultValue={q}
            onKeyDown={(e) => {
              if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value);
            }}
            placeholder="Filter name, phone, APN…"
            data-testid="archived-filter"
            className={inputClass + " w-56"}
          />
          <select
            value={reasonFilter[0] ?? ""}
            onChange={(e) => setParam("dead_reason", e.target.value || null)}
            className={inputClass + " w-44"}
            aria-label="Filter by reason"
            data-testid="reason-filter"
          >
            <option value="">All reasons</option>
            {DEAD_REASONS.map((r) => (
              <option key={r} value={r}>
                {DEAD_REASON_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
      </header>

      {isLoading && <Spinner />}
      {error && <ErrorNote error={error} />}

      {data && (
        <Card className="overflow-hidden">
          {data.items.length === 0 ? (
            <Empty
              title="Nothing archived."
              hint="A lead reaches this page by being moved to Dead with one of the five reasons."
            />
          ) : (
            <div className="-mx-3 -my-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="archived-table">
                <thead>
                  <tr className="border-b bg-surface-2 text-left">
                    <th className="px-2 py-1.5 text-xs font-semibold">Name</th>
                    <th className="px-2 py-1.5 text-xs font-semibold">Phone</th>
                    <th className="px-2 py-1.5 text-xs font-semibold">Reason</th>
                    <th className="px-2 py-1.5 text-xs font-semibold">Market</th>
                    <th className="px-2 py-1.5 text-right text-xs font-semibold">Acres</th>
                    <th className="px-2 py-1.5 text-xs font-semibold">Campaign</th>
                    <th className="px-2 py-1.5 text-xs font-semibold">Last touched</th>
                    <th className="px-2 py-1.5 text-xs font-semibold" />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((l, index) => (
                    <tr
                      key={l.id}
                      className="border-b last:border-b-0 hover:bg-surface-2"
                      data-testid="archived-row"
                    >
                      <td className="max-w-48 truncate px-2 py-1.5">
                        <Link
                          to={leadHref(l.id, list, (data.page - 1) * data.page_size + index)}
                          className="font-medium hover:underline"
                        >
                          {leadName(l)}
                        </Link>
                      </td>
                      <td className="px-2 py-1.5 tabular-nums text-fg-muted">
                        {formatPhone(l.phone_e164)}
                      </td>
                      <td className="px-2 py-1.5">
                        {l.dead_reason ? (
                          <Badge
                            tone={l.dead_reason === "unsubscribed" ? "danger" : "muted"}
                            title={DEAD_REASON_HELP[l.dead_reason]}
                          >
                            {DEAD_REASON_LABEL[l.dead_reason]}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-2 py-1.5">{shown(l.market)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {formatAcres(l.parcel_acres)}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{l.campaign ?? "—"}</td>
                      <td className="px-2 py-1.5 text-xs tabular-nums" data-testid="archived-last-touched">
                        {formatAgo(l.last_touched_at)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <Button
                          size="sm"
                          data-testid={`revive-${l.id}`}
                          disabled={revive.isPending}
                          onClick={() => revive.mutate(l.id)}
                          title="Back to Follow Up, with the reason moved into the lead's history"
                        >
                          Revive
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {data && data.pages > 1 && (
        <div className="mt-2 flex items-center justify-between text-xs text-fg-muted">
          <span>
            Page {data.page} of {data.pages}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              disabled={data.page <= 1}
              onClick={() => setParam("page", String(data.page - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              disabled={data.page >= data.pages}
              onClick={() => setParam("page", String(data.page + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <p className="mt-3 text-[11px] text-fg-muted">
        A dead lead also revives itself: any new inbound message moves it back to Follow Up, clears the
        reason into its history and puts it on Today (§4.4).
      </p>
    </div>
  );
}
